import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('boss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('boss_token');
      localStorage.removeItem('boss_user');
      localStorage.removeItem('boss_permissions');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const login = (email, password) => api.post('/api/boss/core/auth/login', { email, password });
export const getMe = () => api.get('/api/boss/core/auth/me');

// Settings
export const getSettings = () => api.get('/api/boss/core/settings');
export const updateSettings = (data) => api.put('/api/boss/core/settings', data);

// FA Import
export const uploadImport = (formData) => api.post('/api/boss/fa/import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const finalizeImport = (batchId) => api.post(`/api/boss/fa/import/finalize/${batchId}`);
export const getImportBatches = () => api.get('/api/boss/fa/import/batches');

// FA Invoices
export const getInvoices = (params) => api.get('/api/boss/fa/invoices', { params });
export const getInvoice = (id) => api.get(`/api/boss/fa/invoices/${id}`);

// FA Missing
export const getMissingInvoices = (params) => api.get('/api/boss/fa/missing-invoices', { params });
export const resolveMissing = (id, data) => api.put(`/api/boss/fa/missing-invoices/${id}`, data);

// SCRM
export const getParties = (params) => api.get('/api/boss/scrm/parties', { params });
export const createParty = (data) => api.post('/api/boss/scrm/parties', data);
export const updateParty = (id, data) => api.put(`/api/boss/scrm/parties/${id}`, data);
export const getSalesmen = (params) => api.get('/api/boss/scrm/salesmen', { params });
export const createSalesman = (data) => api.post('/api/boss/scrm/salesmen', data);
export const updateSalesman = (id, data) => api.put(`/api/boss/scrm/salesmen/${id}`, data);

// SCL
export const getTransports = (params) => api.get('/api/boss/scl/transports', { params });
export const createTransport = (data) => api.post('/api/boss/scl/transports', data);
export const updateTransport = (id, data) => api.put(`/api/boss/scl/transports/${id}`, data);
export const getStations = (params) => api.get('/api/boss/scl/stations', { params });
export const createStation = (data) => api.post('/api/boss/scl/stations', data);
export const updateStation = (id, data) => api.put(`/api/boss/scl/stations/${id}`, data);
export const uploadBilty = (invoiceId, formData) => api.post(`/api/boss/scl/bilty/upload/${invoiceId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const replaceBilty = (invoiceId, formData) => api.post(`/api/boss/scl/bilty/replace/${invoiceId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getBiltyStatus = (invoiceId) => api.get(`/api/boss/scl/bilty/status/${invoiceId}`);
export const sendWhatsApp = (data) => api.post('/api/boss/scl/whatsapp/send', data);
export const getWhatsAppLogs = (params) => api.get('/api/boss/scl/whatsapp/logs', { params });

// BIA
export const getKpis = (params) => api.get('/api/boss/bia/kpis', { params });
export const getChartAgentwise = (params) => api.get('/api/boss/bia/charts/agentwise', { params });
export const getChartStatewise = (params) => api.get('/api/boss/bia/charts/statewise', { params });
export const getChartMonthwise = (params) => api.get('/api/boss/bia/charts/monthwise', { params });
export const getChartTransportwise = (params) => api.get('/api/boss/bia/charts/transportwise', { params });
export const getChartStationwise = (params) => api.get('/api/boss/bia/charts/stationwise', { params });
export const getChartTopparties = (params) => api.get('/api/boss/bia/charts/topparties', { params });
export const getChartBiltystatus = (params) => api.get('/api/boss/bia/charts/biltystatus', { params });

// DM
export const getDocuments = (params) => api.get('/api/boss/dm/documents', { params });
export const uploadDocument = (formData) => api.post('/api/boss/dm/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteDocument = (id) => api.delete(`/api/boss/dm/documents/${id}`);

// Users
export const getUsers = () => api.get('/api/boss/core/users');
export const createUser = (data) => api.post('/api/boss/core/users', data);
export const updateUser = (id, data) => api.put(`/api/boss/core/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/api/boss/core/users/${id}`);

// Filters
export const getFilterOptions = () => api.get('/api/boss/filters');

// Products
export const getProducts = (params) => api.get('/api/boss/scrm/products', { params });
export const createProduct = (data) => api.post('/api/boss/scrm/products', data);
export const updateProduct = (id, data) => api.put(`/api/boss/scrm/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/api/boss/scrm/products/${id}`);

// Permissions & RBAC
export const getPermissions = () => api.get('/api/boss/core/permissions');

// Dashboard stats
export const getDashboardStats = () => api.get('/api/boss/core/dashboard/stats');

// Import batch detail
export const getImportBatch = (id) => api.get(`/api/boss/fa/import/batches/${id}`);

// ═══ Phase 4 APIs ═══

// Import Reconciliation
export const getImportReconciliation = (batchId) => api.get(`/api/boss/fa/import/reconciliation/${batchId}`);

// WhatsApp Templates + V2
export const getWhatsAppTemplates = () => api.get('/api/boss/scl/whatsapp/templates');
export const sendWhatsAppV2 = (data) => api.post('/api/boss/scl/whatsapp/send-v2', data);

// SSP (Self-Service Portal)
export const getSSPMyInvoices = (params) => api.get('/api/boss/ssp/my-invoices', { params });
export const getSSPMyStats = () => api.get('/api/boss/ssp/my-stats');

// User Profile Linking (for SSP)
export const linkUserProfile = (userId, data) => api.put(`/api/boss/core/users/${userId}/link-profile`, data);

// Authenticated file download helper — uses Authorization header (never puts token in URL)
const downloadExport = async (url, filename) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (err) {
    console.error('Download failed:', err);
    return false;
  }
};

export { downloadExport };

// Exports (file downloads) — all use Authorization header via downloadExport
export const exportInvoicesCSV = (params) => {
  const queryStr = new URLSearchParams(params).toString();
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadExport(`/api/boss/fa/invoices/export/csv?${queryStr}`, `invoices_export_${dateStr}.csv`);
};

export const exportInvoicesExcel = (params) => {
  const queryStr = new URLSearchParams(params).toString();
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadExport(`/api/boss/fa/invoices/export/excel?${queryStr}`, `invoices_export_${dateStr}.xlsx`);
};

export const exportAnalyticsCSV = (params) => {
  const queryStr = new URLSearchParams(params).toString();
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadExport(`/api/boss/bia/export/analytics?${queryStr}`, `analytics_export_${dateStr}.csv`);
};

export const exportProductsCSV = () => {
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadExport('/api/boss/scrm/products/export/csv', `products_export_${dateStr}.csv`);
};

export const exportUsersCSV = () => {
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadExport('/api/boss/core/users/export/csv', `users_export_${dateStr}.csv`);
};
